import { LazyLoading } from "@/controls/LazyLoading";
import { ExcelButton } from "@/controls/buttons/Excel-button";
import {
	dxtToLocalServerTime,
	dxtToUTC,
	getMomentTz,
} from "@dexteel/mesf-core";
import { Button, Grid, Paper, makeStyles } from "@material-ui/core";
import AddBoxIcon from "@material-ui/icons/AddBox";
import FormatListBulletedSharpIcon from "@material-ui/icons/FormatListBulletedSharp";
import {
	CellKeyDownEvent,
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowClickedEvent,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useLogbookContext } from "../context/logbookContext";
import { ENTRY_INITIAL_VALUES } from "../models/EntryInitialValue";
import { RemoveEntryDialog } from "./dialog/remove-entry-dialog";
import { Filters } from "./filters/filters";

const useStyles = makeStyles((theme) => ({
	container: {
		padding: theme.spacing(0, 2.5),
	},
	root: {
		display: "flex",
		flexDirection: "row",
		width: "100%",
		height: "86vh",
		"& h3": {
			display: "inline-block",
			marginTop: 0,
			paddingBotton: 0,
			paddingLeft: 20,
			border: "none",
			userSelect: "none",
		},
		"& .content-wrapper": {
			border: "none",
		},
		"& .ag-header-label-icon": {
			display: "none",
		},
		"& .ag-floating-filter-button": {
			padding: 0,
			marginLeft: "0px",
		},
		"& .ag-icon-menu": {
			display: "none",
		},
		"& .ag-root-wrapper": {
			borderRadius: 3,
		},
		"& .ag-header-cell": {
			padding: "0 15px !important",
			textAlign: "center !important",
			fontSize: 10,
		},
		"& .ag-header-cell-label": {
			justifyContent: "center",
		},
		"& .ag-cell": {
			padding: "0px !important",
			textAlign: "center !important",
			fontSize: 12,
			lineBreak: "auto",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			border: "0 !important",
			wordBreak: "break-word",
		},
		"& .ag-comments": {
			display: "flex",
			justifyContent: "flex-start",
			alignItems: "center",
		},
		"& .ag-selection-checkbox": {
			marginLeft: "10px !important",
		},
		"& .ag-paging-panel": {
			height: "60px !important",
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: "0 16px",
		},
		"& .ag-paging-row-summary-panel": {
			color: "#FFFF !important",
		},
	},
	gridContainer: {
		width: "35%",
		height: "100%",
		borderRadius: "0.75rem",
		marginRight: 30,
		marginLeft: 10,
		marginTop: 10,
		display: "flex",
		flexDirection: "column",
	},
	agGridContainer: {
		position: "relative",
		flex: 1,
	},
	btnAdd: {
		position: "absolute",
		padding: 0,
		bottom: 15,
		left: 11,
	},
	btnExcel: {
		position: "absolute",
		bottom: 12,
		right: 15,
	},
	paginationContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		height: "60px",
		padding: "0 16px",
		borderTop: "1px solid #BDC3C7",
	},
	addButton: {
		display: "flex",
		alignItems: "center",
	},
	filtersContainer: {
		height: "auto",
		marginBottom: 20,
	},
	outletContainer: {
		width: "60%",
		height: "100%",
	},
}));

const moment = getMomentTz();
export const Logbook = ({
	searchEntries,
	isLoading,
}: {
	searchEntries: Function;
	isLoading: boolean;
}) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const classes = useStyles();
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);

	const params = useParams();
	const navigate = useNavigate();
	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
	const [showRemoveEntryDialog, setShowRemoveEntryDialog] = useState(false);
	const { EntryId } = useParams();

	const {
		state: { EntriesList, EntrySelectedInTable, loadingTable, exportExcel },
		actions: {
			setEntrySelectedInTable,
			setEntryTitleToDelete,
			setEntryIdToDelete,
			setIsEditing,
			setExportExcel,
		},
	} = useLogbookContext();

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			flex: 1,
		};
	}, []);

	const icons = useMemo(() => {
		return {
			filter: '<i class="fas fa-bars"></i>',
		};
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).contextMenuFactory.showMenu(
			params.node,
			params.column,
			undefined,
			e,
		);
	};

	const rowClicked = (event: RowClickedEvent) => {
		setEntrySelectedInTable(event?.data);
		setIsEditing(false);
		navigate(`${event.data?.EntryId}${location.search}`);
	};

	const onDobleClicked = (event: RowDoubleClickedEvent) => {
		setEntrySelectedInTable(event?.data);
		setIsEditing(true);
		navigate(`${event.data?.EntryId}${location.search}`);
	};

	const addEntry = () => {
		setEntrySelectedInTable({
			...ENTRY_INITIAL_VALUES,
			Shift: EntrySelectedInTable.Shift,
			Crew: EntrySelectedInTable.Crew,
			Author: EntrySelectedInTable.Author,
		});
		navigate(`new${location.search}`);
	};

	const onCellKeyDown = useCallback(
		(event: CellKeyDownEvent) => {
			const { api, node, event: keyEvent } = event;
			if (!keyEvent) return;
			const key = (keyEvent as KeyboardEvent).key;

			if (key === "ArrowUp" || key === "ArrowDown") {
				keyEvent.preventDefault();
				const currentIndex = node.rowIndex !== null ? node.rowIndex : -1;
				const nextRowIndex =
					key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1;
				const nextNode = api.getDisplayedRowAtIndex(nextRowIndex);

				if (nextNode) {
					nextNode.setSelected(true);
					api.ensureIndexVisible(nextRowIndex);
					setEntrySelectedInTable(nextNode.data);
					navigate(`${nextNode.data?.EntryId}${location.search}`);
				}
			}
		},
		[navigate, setEntrySelectedInTable],
	);

	const getLogbookContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data: any = params.node?.data;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];
			if (data) {
				result.push({
					name: "<strong>Edit Entry</strong>",
					action: () => {
						setEntrySelectedInTable(data);
						navigate(`${data?.EntryId}${location.search}`);
						setIsEditing(true);
					},
					icon: '<i class="fas fa-edit"></i>',
				});
			}

			result.push({
				name: "Add Entry",
				action: () => {
					setEntrySelectedInTable({
						...ENTRY_INITIAL_VALUES,
						CreateTimestamp: new Date(),
						Shift: EntrySelectedInTable.Shift,
						Crew: EntrySelectedInTable.Crew,
						Author: EntrySelectedInTable.Author,
					});
					setIsEditing(true);
					navigate(`new${location.search}`);
				},
				icon: '<i class="fas fa-plus"></i>',
			});

			if (data) {
				{
					result.push({
						name: "Remove ",
						action: () => {
							setEntryIdToDelete(data.EntryId);
							setEntryTitleToDelete(data.Title);
							setShowRemoveEntryDialog(true);
						},
						icon: '<i class="fas fa-trash"></i>',
					});
				}
			}
			if (!data) {
				result.push({
					name: "Export to Excel",
					action: customExcelExport,
					icon: '<i class="fas fa-file-excel"></i>',
				});
			}

			return result;
		},
		[gridApi],
	);

	useEffect(() => {
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "CreateTimestamp",
			headerName: "Created",
			minWidth: 120,
			maxWidth: 120,
			flex: 1,
			valueGetter: (params) =>
				dxtToLocalServerTime(
					`${params.data.CreateTimestamp}`,
					"MM/dd/yy HH:mm",
				),
		}),
			newColumns.push({
				field: "AssetName",
				headerName: "Asset",
				sortable: true,
				flex: 1,
				minWidth: 70,
			}),
			newColumns.push({
				field: "Title",
				headerName: "Title",
				sortable: false,
				flex: 7,
				minWidth: 160,
				wrapText: true,
				autoHeight: true,
			}),
			newColumns.push({
				field: "Author",
				headerName: "Created By",
				sortable: false,
				flex: 1,
				minWidth: 115,
			}),
			newColumns.push({
				field: "Content",
				headerName: "Content",
				hide: true,
				suppressColumnsToolPanel: true,
				suppressFiltersToolPanel: true,
				suppressHeaderMenuButton: true,
				suppressSizeToFit: true,
				valueGetter: (params) => params.data.Content,
			});
		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid
							item
							xs={12}
							md={12}
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid item xs={12} md={12}>
									<Button fullWidth onClick={(e) => onActionsClick(e, params)}>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				);
			},
		});
		setColumnDefs(newColumns);
	}, [EntryId]);

	useEffect(() => {
		if (isLoading || loadingTable) {
			gridApi?.showLoadingOverlay();
		} else gridApi?.hideOverlay();
	}, [isLoading, gridApi, loadingTable]);

	const customExcelExport = useCallback(() => {
		const params = {
			fileName: "Logbook_Export.xlsx",
			columnKeys: [
				"CreateTimestamp",
				"Shift",
				"Crew",
				"Title",
				"CreatedBy",
				"Author",
				"Content",
			],
			processCellCallback: (params: any) => {
				if (params.column.colId === "CreateTimestamp") {
					return moment(params.value).format("MM/DD/yy - HH:mm");
				}
				return params.value;
			},
		};
		gridApi?.exportDataAsExcel(params);
	}, [gridApi]);

	useEffect(() => {
		if (exportExcel) {
			customExcelExport();
			setExportExcel(false);
		}
	}, [exportExcel, customExcelExport, setExportExcel]);

	return (
		<>
			<Grid container spacing={1} style={{ backgroundColor: "#F2F4F7" }}>
				<Grid item xs={12} md={5} container spacing={1}>
					<Grid item container>
						<Filters searchEntries={searchEntries} />
					</Grid>
					<Grid item xs={12}>
						<Paper elevation={1} className={classes.agGridContainer}>
							<div style={{ height: "60vh", padding: "5px 0 60px" }}>
								<div style={gridStyle}>
									<AgGridReact
										rowData={EntriesList}
										columnDefs={columnDefs}
										animateRows={true}
										loading={isLoading}
										getContextMenuItems={(e: any) =>
											getLogbookContextMenuItems(e)
										}
										icons={icons}
										onRowClicked={rowClicked}
										onRowDoubleClicked={onDobleClicked}
										rowSelection="single"
										onGridReady={(params) => setGridApi(params.api)}
										onCellKeyDown={onCellKeyDown}
										getRowId={(params) => params.data.EntryId}
									/>
								</div>
								<div className={classes.btnAdd}>
									<AddBoxIcon
										onClick={() => {
											addEntry();
											setIsEditing(true);
										}}
										color="primary"
										style={{
											width: 35,
											height: 35,
											marginRight: "0",
											cursor: "pointer",
										}}
									/>
								</div>
								<div className={classes.btnExcel}>
									<ExcelButton onClick={() => setExportExcel(!exportExcel)} />
								</div>
							</div>
						</Paper>
					</Grid>
				</Grid>
				<Grid item xs={12} md={7}>
					<Outlet key={`entry-${params?.EntryId}`} />
				</Grid>
			</Grid>
			<RemoveEntryDialog
				show={showRemoveEntryDialog}
				onHide={(shouldUpdate) => {
					if (shouldUpdate) {
						searchEntries();
					}
					setShowRemoveEntryDialog(false);
				}}
			/>
		</>
	);
};
